import React from 'react';
import { Check, Loader2, AlertCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AutoSaveIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  className?: string;
}

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
  isSaving,
  lastSaved,
  hasUnsavedChanges,
  className
}) => {
  const getStatusInfo = () => {
    if (isSaving) {
      return {
        icon: Loader2,
        text: 'Guardando...',
        className: 'text-blue-600',
        iconClassName: 'animate-spin'
      };
    }

    if (hasUnsavedChanges) {
      return {
        icon: Clock,
        text: 'Cambios sin guardar',
        className: 'text-orange-600',
        iconClassName: ''
      };
    }

    if (lastSaved) {
      const timeAgo = getTimeAgo(lastSaved);
      return {
        icon: Check,
        text: `Guardado ${timeAgo}`,
        className: 'text-green-600',
        iconClassName: ''
      };
    }

    return {
      icon: Check,
      text: 'Sin cambios',
      className: 'text-gray-500',
      iconClassName: ''
    };
  };

  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'hace un momento';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `hace ${diffInMinutes} min`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `hace ${diffInHours}h`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return `hace ${diffInDays}d`;
  };

  const statusInfo = getStatusInfo();
  const Icon = statusInfo.icon;

  return (
    <div className={cn(
      'flex items-center gap-2 text-sm font-medium',
      statusInfo.className,
      className
    )}>
      <Icon className={cn('h-4 w-4', statusInfo.iconClassName)} />
      <span>{statusInfo.text}</span>
    </div>
  );
};
